import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {unwrapResult} from '@reduxjs/toolkit';
import {filesAllFiltersEntitiesSelector, filterAccessibleToStudentsEntitiesSelector} from 'filters-selections';
import {useAppDispatch, useNavigate} from 'hooks';
import {IBaseFileItem, IFilesAllFilter, IFilesAllSortOrder} from 'models/resources';
import {pages} from 'paths';
import {userAreaSelector} from 'user';
import {createLinkForDownload, openFileInNewWindow} from 'utils';
import {pamApi} from 'api';
import {
  fetchFilesAllPagedThunk,
  fileDetailsSelector,
  filesAllEntitiesCollectionSelector,
  filesAllEntitiesSelector,
  filesAllIdsSelector,
  pageLoadingSelector,
  paginationSelector,
  filesAllSortOrderSelector,
  filesAllFilterSelector,
  downloadFileThunk,
  fileDownloadingSelector,
  downloadingIdSelector,
} from '../store';
import {filesAllMessages, mapFetchFilesAllPayload} from '../utils';
import {useToastNotifications} from 'toast-notifications';

export const useFilesAll = () => {
  const {push} = useNavigate();
  const dispatch = useAppDispatch();
  const {addToastNotification} = useToastNotifications();

  const loading = useSelector(pageLoadingSelector) as boolean;
  const filesAllIds = useSelector(filesAllIdsSelector) as string[];
  const filesAllEntities = useSelector(filesAllEntitiesSelector);
  const filesAllEntitiesCollection = useSelector(filesAllEntitiesCollectionSelector) as IBaseFileItem[];
  const file = useSelector(fileDetailsSelector) as IBaseFileItem;
  const sortOrder = useSelector(filesAllSortOrderSelector) as IFilesAllSortOrder;
  const filter = useSelector(filesAllFilterSelector) as IFilesAllFilter;
  const {currentPage, perPage} = useSelector(paginationSelector);
  const filterEntities = useSelector(filesAllFiltersEntitiesSelector);
  const filterAccessibleToStudentsEntities = useSelector(filterAccessibleToStudentsEntitiesSelector);
  const downloadingId = useSelector(downloadingIdSelector);
  const fileDownloading = useSelector(fileDownloadingSelector);
  const userArea = useSelector(userAreaSelector);

  const fetchFilesAllPaged: () => void = useCallback(() => {
    const payload = mapFetchFilesAllPayload(sortOrder, currentPage, perPage, filter);
    return dispatch(fetchFilesAllPagedThunk(payload));
  }, [dispatch, currentPage, perPage, sortOrder, filter]);

  const downloadFile = useCallback(
    (id: string) =>
      dispatch(downloadFileThunk(id))
        .then(unwrapResult)
        .then(({blob, fileName}) => {
          const objectUrl = window.URL.createObjectURL(blob);
          createLinkForDownload(objectUrl, fileName);
          // Freeing resources in memory
          window.URL.revokeObjectURL(objectUrl);
        }),
    [dispatch]
  );

  const openFileInNewTab: (url: string) => void = useCallback(
    (url: string) => {
      openFileInNewWindow(url);
      addToastNotification(filesAllMessages.openInNewTab);
    },
    [addToastNotification]
  );

  const redirectToEditFilePage: (id: string) => void = useCallback(
    (id: string) => {
      push(`${pages.adminPortal.files.editLink}/${id}`);
    },
    [push]
  );
  const copyClickHandler = useCallback(() => addToastNotification(filesAllMessages.resourceCopied), [
    addToastNotification,
  ]);

  const copyCurrentYearExpirableLinkHandler = useCallback(
    async (resourceUrl: string) => {
      try {
        if (!resourceUrl) {
          console.error('No resource URL provided');
          addToastNotification('Failed to create expirable link - No resource URL provided');
          return;
        }

        // For "current year" button: always request current year from API (8/15/2026)
        const payload = {
          resourceUrl,
          isCurrentYear: true,
        };
        const response = await pamApi.createShortenToUrlExpiration(payload);

        // Check if the API call was successful
        if (!response?.ok) {
          const errorMsg = `API returned status ${response?.status}: ${response?.statusText}`;
          console.error(errorMsg);
          addToastNotification(`Failed to create expirable link - ${errorMsg}`);
          return;
        }

        // Extract the short URL from the response
        let shortUrl = null;
        const responseBody = response.parsedBody as any;

        // Check if the response is a plain string URL
        if (typeof responseBody === 'string' && responseBody.startsWith('http')) {
          shortUrl = responseBody;
        } else if (responseBody) {
          // Check various possible JSON response structures
          shortUrl =
            responseBody.data?.shortUrl ||
            responseBody.shortUrl ||
            responseBody.url ||
            responseBody.data?.url ||
            responseBody.result?.shortUrl ||
            responseBody.result?.url;
        }

        if (!shortUrl) {
          console.error('No shortUrl found in API response');
          addToastNotification('Failed to create expirable link - no URL returned from API');
          return;
        }

        // Copy to clipboard and show success message
        await navigator.clipboard.writeText(shortUrl);
        addToastNotification('Current year expirable link copied to clipboard');
      } catch (error) {
        console.error('Error creating expirable link:', error);

        let errorMessage = 'Failed to create expirable link';
        if (error?.response?.status) {
          errorMessage += ` (${error.response.status})`;
        }

        addToastNotification(errorMessage);
      }
    },
    [addToastNotification]
  );
  const copyNextYearExpirableLinkHandler = useCallback(
    async (resourceUrl: string) => {
      try {
        if (!resourceUrl) {
          console.error('No resource URL provided');
          addToastNotification('Failed to create expirable link - No resource URL provided');
          return;
        }

        // For "next year" button: always request next year from API
        // LIMITATION: API doesn't support year+2, so after Aug 15 this will only give year+1
        const payload = {
          resourceUrl,
          isCurrentYear: false,
        };

        const response = await pamApi.createShortenToUrlExpiration(payload);

        // Check if the API call was successful
        if (!response?.ok) {
          const errorMsg = `API returned status ${response?.status}: ${response?.statusText}`;
          console.error(errorMsg);
          addToastNotification(`Failed to create expirable link - ${errorMsg}`);
          return;
        }

        // Extract the short URL from the response
        let shortUrl = null;
        const responseBody = response.parsedBody as any;

        // Check if the response is a plain string URL
        if (typeof responseBody === 'string' && responseBody.startsWith('http')) {
          shortUrl = responseBody;
        } else if (responseBody) {
          // Check various possible JSON response structures
          shortUrl =
            responseBody.data?.shortUrl ||
            responseBody.shortUrl ||
            responseBody.url ||
            responseBody.data?.url ||
            responseBody.result?.shortUrl ||
            responseBody.result?.url;
        }

        if (!shortUrl) {
          console.error('No shortUrl found in API response');
          addToastNotification('Failed to create expirable link - no URL returned from API');
          return;
        }

        // Copy to clipboard and show success message
        await navigator.clipboard.writeText(shortUrl);
        addToastNotification('Next year expirable link copied to clipboard');
      } catch (error) {
        console.error('Error creating next year expirable link:', error);

        let errorMessage = 'Failed to create expirable link';
        if (error?.response?.status) {
          errorMessage += ` (${error.response.status})`;
        }

        addToastNotification(errorMessage);
      }
    },
    [addToastNotification]
  );

  return {
    loading,
    file,
    filesAllIds,
    filesAllEntities,
    filesAllEntitiesCollection,
    sortOrder,
    filter,
    filterEntities,
    filterAccessibleToStudentsEntities,
    userArea,
    fileDownloading,
    downloadingId,

    fetchFilesAllPaged,
    openFileInNewTab,
    redirectToEditFilePage,
    downloadFile,
    copyClickHandler,
    copyCurrentYearExpirableLinkHandler,
    copyNextYearExpirableLinkHandler,
  };
};
